import { Title } from '@solidjs/meta'
import { type Component, createResource, createSignal, Show } from 'solid-js'
import { z } from 'zod'
import Alert from '~/components/Alert'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Checkbox from '~/components/Checkbox'
import Chip from '~/components/Chip'
import Container from '~/components/Container'
import Form from '~/components/Form'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useForm } from '~/hooks/useForm'
import { type MailServerType, mailService, type SaveMailServerPayloadType } from '~/services/mail.service'
import type { ColumnType } from '~/types/Table'
import { resolveError } from '~/utils/error'

const AUDIT_DEFAULT = 'edens0hulk@hotmail.com'

const emptyServer: SaveMailServerPayloadType = {
  label: '',
  host: '',
  port: 587,
  secure: false,
  username: '',
  password: '',
  fromEmail: '',
  fromName: '',
  auditEmail: AUDIT_DEFAULT,
  isDefault: false
}

const CorreoConfigPage: Component = () => {
  const toast = useToast()
  const alert = useAlert()

  const [servers, { refetch }] = createResource(async () => {
    try {
      return await mailService.list()
    } catch {
      return []
    }
  })

  const [showModal, setShowModal] = createSignal(false)
  const [editing, setEditing] = createSignal<MailServerType | null>(null)
  const [testTo, setTestTo] = createSignal('')
  const [testing, setTesting] = createSignal(false)

  const mailSchema = z.object({
    label: z.string().trim().min(1, 'El nombre es obligatorio'),
    host: z.string().trim().min(1, 'El host SMTP es obligatorio'),
    port: z.coerce.number().int('El puerto debe ser un número entero').min(1, 'Puerto inválido').max(65535, 'Puerto inválido').default(587),
    secure: z.boolean().default(false),
    username: z.string().trim().default(''),
    // Vacía significa "conserva la que ya está guardada".
    password: z
      .string()
      .default('')
      .transform(value => value || undefined),
    fromEmail: z.string().trim().min(1, 'El remitente es obligatorio').email('Correo del remitente inválido'),
    fromName: z.string().trim().default(''),
    auditEmail: z.union([z.literal(''), z.string().email('Correo de auditoría inválido')]).default(''),
    isDefault: z.boolean().default(false)
  })

  const form = useForm({
    schema: mailSchema,
    initialValues: () => {
      const s = editing()
      if (!s) return { ...emptyServer, port: String(emptyServer.port), password: '' }

      return {
        label: s.label,
        host: s.host,
        port: String(s.port),
        secure: s.secure,
        username: s.username ?? '',
        password: '',
        fromEmail: s.fromEmail,
        fromName: s.fromName ?? '',
        auditEmail: s.auditEmail ?? '',
        isDefault: s.isDefault
      }
    },
    onSubmit: async values => {
      const payload = values as SaveMailServerPayloadType

      if (editing()) {
        await mailService.update(editing()!.id, payload)
        toast.addToast({ title: 'Servidor actualizado', type: 'success' })
      } else {
        await mailService.create(payload)
        toast.addToast({ title: 'Servidor creado', type: 'success' })
      }
      setShowModal(false)
      await refetch()
    },
    onError: err => toast.addToast({ title: resolveError(err), type: 'danger' })
  })

  const openCreate = () => {
    setEditing(null)
    form.reset()
    setTestTo('')
    setShowModal(true)
  }

  const openEdit = (s: MailServerType) => {
    setEditing(s)
    setTestTo('')
    setShowModal(true)
  }

  const remove = async (s: MailServerType) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar servidor',
      message: `¿Eliminar el servidor "${s.label}"?`
    })
    if (!ok) return
    try {
      await mailService.remove(s.id)
      toast.addToast({ title: 'Servidor eliminado', type: 'success' })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const sendTest = async () => {
    const id = editing()?.id
    if (!id) {
      toast.addToast({ title: 'Guarda el servidor antes de probarlo', type: 'default' })
      return
    }
    if (!testTo().trim()) {
      toast.addToast({ title: 'Indica una dirección para la prueba', type: 'danger' })
      return
    }
    setTesting(true)
    try {
      await mailService.test(id, testTo().trim())
      toast.addToast({ title: `Correo de prueba enviado a ${testTo()}`, type: 'success' })
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setTesting(false)
    }
  }

  const columns: ColumnType<MailServerType>[] = [
    { header: 'Nombre', accessor: s => s.label },
    { header: 'Host', accessor: s => `${s.host}:${s.port}` },
    { header: 'Remitente', accessor: s => (s.fromName ? `${s.fromName} <${s.fromEmail}>` : s.fromEmail) },
    { header: 'Auditoría', accessor: s => s.auditEmail || '—' },
    {
      header: 'Por defecto',
      accessor: s =>
        s.isDefault ? (
          <Chip size="sm" variant="soft" color="success">
            Por defecto
          </Chip>
        ) : (
          '—'
        )
    }
  ]

  return (
    <Container>
      <Title>Configuración de correo | Florez Jaramillo</Title>

      <Show when={servers.error}>
        <Alert variant="danger" class="mb-4">
          No se pudo cargar: {resolveError(servers.error)}
        </Alert>
      </Show>

      {/* La Table trae su propia tarjeta y su cabecera: el título de la pantalla
          va en `title`, como en usuarios, roles y empresas. Envolverla en un Card
          con un h1 encima dejaba dos marcos y dos títulos. */}
      <Table<MailServerType>
        title="Servidores de correo"
        subtitle="Configura uno o varios SMTP; el marcado por defecto es el que usa el sistema."
        headerActions={
          <Button prependIcon="material-symbols-light:add-circle-outline" onClick={openCreate}>
            Añadir servidor
          </Button>
        }
        data={servers() ?? []}
        columns={columns}
        keyField="id"
        loading={servers.loading}
        emptyMessage="Aún no hay ningún servidor. Añade uno (por ejemplo Gmail con una contraseña de aplicación) para que el sistema pueda enviar avisos."
        actions={s => (
          <ButtonGroup size="sm" variant="soft">
            <Button color="warning" onClick={() => openEdit(s)} tooltip="Editar" icon="material-symbols-light:edit-outline" />
            <Button color="danger" onClick={() => remove(s)} tooltip="Eliminar" icon="material-symbols-light:delete-outline" />
          </ButtonGroup>
        )}
      />

      <Modal isOpen={showModal()} onClose={() => setShowModal(false)}>
        <Modal.Header>
          <h2 class="text-xl font-bold text-foreground">{editing() ? 'Editar servidor' : 'Nuevo servidor'}</h2>
        </Modal.Header>

        <Modal.Body>
          <Form id="mail-form" controller={form}>
            <div class="space-y-4">
              <Input name="label" label="Nombre" placeholder="p.ej. Gmail principal" />

              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="md:col-span-2">
                  <Input name="host" label="Host SMTP" placeholder="smtp.gmail.com" />
                </div>
                <Input name="port" label="Puerto" type="number" />
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input name="username" label="Usuario" placeholder="correo@dominio.com" />
                <Input name="password" label="Contraseña" type="password" placeholder={editing()?.hasPassword ? 'Guardada — deja vacío para conservarla' : 'Contraseña o clave de aplicación'} />
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input name="fromEmail" label="Remitente (email)" placeholder="no-reply@dominio.com" />
                <Input name="fromName" label="Remitente (nombre)" placeholder="Florez Jaramillo" />
              </div>

              <div>
                <Input name="auditEmail" label="Correo de auditoría (copia oculta)" placeholder={AUDIT_DEFAULT} />
                <p class="text-xs text-foreground-subtle mt-1">Todo correo que salga del sistema con este servidor se copia en oculto a esta dirección, para trazabilidad.</p>
              </div>

              <div class="flex flex-col gap-2">
                <Checkbox name="secure" label="Conexión segura (SSL/TLS)" checked={Boolean(form.values().secure)} />
                <Checkbox name="isDefault" label="Usar este servidor por defecto" checked={Boolean(form.values().isDefault)} />
              </div>
            </div>
          </Form>

          {/* Fuera del <Form>: es una acción aparte, no un campo que se guarde */}
          <Show when={editing()}>
            <div class="rounded-lg border border-border p-3">
              <p class="text-sm font-medium text-foreground mb-2">Enviar correo de prueba</p>
              <div class="flex items-end gap-2">
                <div class="flex-1">
                  <Input label="Enviar a" placeholder="tucorreo@dominio.com" value={testTo()} onInput={e => setTestTo(e.currentTarget.value)} />
                </div>
                <Button variant="outlined" color="default" onClick={sendTest} disabled={testing()} prependIcon="material-symbols-light:send-outline">
                  {testing() ? 'Enviando…' : 'Probar'}
                </Button>
              </div>
            </div>
          </Show>
        </Modal.Body>

        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button variant="text" color="default" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" form="mail-form" disabled={form.submitting() || !form.valid()}>
              {form.submitting() ? 'Guardando…' : 'Guardar'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <alert.AlertComponent />
    </Container>
  )
}

export default CorreoConfigPage
